import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PLANS } from "@/lib/plans";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

// Today's AI usage, read from the server-side counter (GET /ai/usage) — the same
// source enforce_ai_limit uses, so it can never disagree with the real quota.
export function AiUsageCard() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [u, setU] = useState(null);

  const load = useCallback(() => {
    api.get("/ai/usage").then(({ data }) => setU(data)).catch(() => setU(null));
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!u) return null;

  const pct = u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
  const low = u.remaining <= Math.max(2, Math.ceil(u.limit * 0.2));
  const exhausted = u.remaining <= 0;
  const barTone = exhausted ? "bg-rose-500" : low ? "bg-amber-500" : "bg-primary";
  const planName = (PLANS[u.tier]?.name?.[language]) || (PLANS[u.tier]?.name?.en) || u.tier;
  const canUpgrade = u.tier !== "pro" && !!u.next_tier;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="ai-usage-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Gauge className="h-4 w-4 text-primary" /> {t("usage_title")}
          </div>
          <div className="mt-2 flex items-end gap-2">
            <span className="font-display text-2xl font-extrabold text-slate-950" data-testid="ai-usage-count">{u.used}</span>
            <span className="pb-0.5 text-sm text-slate-400">/ {u.limit} · {planName}</span>
          </div>
          <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid="ai-usage-bar">
            <div className={`h-full rounded-full ${barTone} transition-all duration-500`} style={{ width: `${pct}%` }} />
          </div>
          <p className={`mt-2 text-sm ${exhausted ? "text-rose-600" : low ? "text-amber-600" : "text-slate-500"}`} data-testid="ai-usage-remaining">
            {exhausted
              ? t("usage_exhausted")
              : t("usage_remaining").replace("{n}", u.remaining)}
          </p>
        </div>
        {canUpgrade && (
          <Button
            onClick={() => navigate("/membership")}
            data-testid="ai-usage-upgrade-btn"
            variant={low ? "default" : "outline"}
            className={`min-h-[44px] shrink-0 gap-1.5 rounded-full ${low ? "bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300" : ""}`}
          >
            <Sparkles className="h-4 w-4" /> {t("usage_upgrade")}
          </Button>
        )}
      </div>
    </div>
  );
}
